import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiUtils, getToken } from "../../../utils/apiUtils";
import useRepGuardClients from "./useRepGuardClients";

function formatDate(value) {
  if (!value) {
    return "Unknown";
  }

  return new Date(value).toLocaleString();
}

function formatDuration(ms) {
  if (!ms) {
    return "n/a";
  }

  const minutes = Math.round(ms / 60000);
  if (minutes < 60) {
    return `${minutes} min`;
  }

  return `${(minutes / 60).toFixed(1)} hr`;
}

function formatAction(action) {
  return action.replace("draft.", "").replace(/_/g, " ");
}

export default function RepGuardAnalyticsHistory() {
  const [clientFilter, setClientFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [permissions, setPermissions] = useState({
    canExportHistory: false,
    role: "viewer",
  });
  const {
    clients,
    clientError,
  } = useRepGuardClients();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = clientFilter !== "all" ? { clientId: clientFilter } : {};
      const [analyticsResponse, historyResponse] = await Promise.all([
        apiUtils.get("/api/extension/analytics", { params }),
        apiUtils.get("/api/extension/history", {
          params: {
            ...params,
            limit: 30,
          },
        }),
      ]);

      setAnalytics(analyticsResponse.data?.data || null);
      setHistory(historyResponse.data?.data?.history || []);
      setPermissions(analyticsResponse.data?.data?.permissions || {
        canExportHistory: false,
        role: "viewer",
      });
    } catch (loadError) {
      setError(loadError.message || "Failed to load RepGuard analytics.");
    } finally {
      setLoading(false);
    }
  }, [clientFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const metricCards = useMemo(() => {
    const data = analytics || {};
    return [
      { label: "Prevented posts", value: data.preventedPosts ?? 0 },
      { label: "Drafts blocked", value: data.auditSummary?.blocked ?? 0 },
      { label: "Reviews requested", value: data.auditSummary?.reviewRequested ?? 0 },
      { label: "Overrides accepted", value: data.auditSummary?.overrides ?? 0 },
      { label: "Approved reviews", value: data.reviewSummary?.approved ?? 0 },
      { label: "Avg review turnaround", value: formatDuration(data.turnaround?.avgTurnaroundMs) },
    ];
  }, [analytics]);

  const handleExport = async () => {
    try {
      setError("");
      const query = new URLSearchParams();
      if (clientFilter !== "all") {
        query.set("clientId", clientFilter);
      }

      const baseUrl = process.env.REACT_APP_API_URL
        || (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1"
          ? `${window.location.protocol}//${window.location.hostname}:4000`
          : "http://localhost:4000");

      const headers = { "Content-Type": "application/json" };
      const token = getToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${baseUrl}/api/extension/history/export?${query.toString()}`, {
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to export CSV.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "repguard-history.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(exportError.message || "Failed to export RepGuard history.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-t border-border pt-6 first:border-0 first:pt-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">RepGuard Analytics and History</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Track prevented posts, review turnaround, and recent RepGuard moderation events by client.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-foreground">Client</label>
            <select
              value={clientFilter}
              onChange={(event) => setClientFilter(event.target.value)}
              className="rounded-lg border border-input px-3 py-2 text-sm"
            >
              <option value="all">All clients</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleExport}
              disabled={!permissions.canExportHistory}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-neutral-400"
            >
              Export CSV
            </button>
          </div>
        </div>

        {clientError && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
            {clientError}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
            {error}
          </div>
        )}
        {!permissions.canExportHistory && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
            Your role is <span className="font-semibold">{permissions.role}</span>. Only admins, managers, and analysts can export RepGuard history.
          </div>
        )}

        {loading ? (
          <div className="mt-6 text-muted-foreground">Loading analytics...</div>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {metricCards.map((card) => (
              <div key={card.label} className="rounded-xl border border-border bg-muted p-4">
                <div className="text-sm text-muted-foreground">{card.label}</div>
                <div className="mt-2 text-lg font-semibold text-foreground">{card.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border pt-6 first:border-0 first:pt-0">
        <h3 className="text-base font-semibold text-foreground">Recent history</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Latest extension warnings, blocks, overrides, and review decisions.
        </p>

        {loading ? (
          <div className="mt-6 text-muted-foreground">Loading history...</div>
        ) : history.length === 0 ? (
          <div className="mt-6 rounded-xl border border-dashed border-input p-8 text-center text-muted-foreground">
            No RepGuard history found for the selected scope.
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Risk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {history.map((entry) => (
                  <tr key={entry._id}>
                    <td className="px-4 py-3 text-sm text-foreground">{formatDate(entry.timestamp)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{entry.metadata?.clientName || "n/a"}</td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{formatAction(entry.action)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{entry.metadata?.reason || entry.changes?.summary || "n/a"}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{entry.risk?.level || "low"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
